import React, { useState } from "react";
import {
  TextField,
  Select,
  MenuItem,
  Button,
  Typography,
  FormControl,
  InputLabel,
  Box,
  IconButton,
  Paper,
  Stack,
  alpha,
  useTheme,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import {
  AddCircleOutline as AddIcon,
  OilBarrel as CargoIcon,
  People as CrewIcon,
  Anchor as VoyageIcon,
  DeleteOutline as DeleteIcon,
} from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";

const TripForm = ({ tripData, onSave, onCancel }) => {
  const { t } = useTranslation("currentvoyage");
  const theme = useTheme();

  // Helper för att skapa ett unikt cargo-objekt
  const createEmptyCargo = () => ({
    cargoId: `cargo-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`,
    cargo: "",
    quantity: "",
    unit: "m3 (obs)",
    showTolerance: false,
    tolerances: [{ toleranceValue: "", toleranceUnit: "%" }],
  });

  // State-initiering
  const [formData, setFormData] = useState({
    // Om tripData finns (edit), behåll ID och Index, annars generera nytt
    tripId: tripData?.tripId || `trip-${Date.now()}`,
    tripIndex: tripData?.tripIndex || null,
    date: tripData?.date || dayjs().format("YYYY-MM-DD"),
    operationType: tripData?.operationType || "Loading",
    from: tripData?.from || "",
    to: tripData?.to || "",
    cpDated: tripData?.cpDated || "",
    supplierReceiver: tripData?.supplierReceiver || "",
    jetty: tripData?.jetty || "",
    cargos:
      tripData?.cargos && tripData.cargos.length > 0
        ? tripData.cargos
        : [createEmptyCargo()],
    crew: tripData?.crew || { chiefOfficer: "", masterName: "" },
    status: tripData?.status || "planned",
  });

  // --- CARGO LOGIK ---
  const addCargoRow = () => {
    setFormData((prev) => ({
      ...prev,
      cargos: [...prev.cargos, createEmptyCargo()],
    }));
  };

  const removeCargoRow = (id) => {
    if (formData.cargos.length === 1) return;
    setFormData((prev) => ({
      ...prev,
      cargos: prev.cargos.filter((c) => c.cargoId !== id),
    }));
  };

  const handleCargoChange = (index, field, value) => {
    const updatedCargos = [...formData.cargos];
    updatedCargos[index] = { ...updatedCargos[index], [field]: value };
    setFormData((prev) => ({ ...prev, cargos: updatedCargos }));
  };

  const toggleTolerance = (index) => {
    const updatedCargos = [...formData.cargos];
    updatedCargos[index].showTolerance = !updatedCargos[index].showTolerance;
    setFormData((prev) => ({ ...prev, cargos: updatedCargos }));
  };

  const handleToleranceChange = (cargoIndex, field, value) => {
    const updatedCargos = [...formData.cargos];
    if (!updatedCargos[cargoIndex].tolerances) {
      updatedCargos[cargoIndex].tolerances = [
        { toleranceValue: "", toleranceUnit: "%" },
      ];
    }
    updatedCargos[cargoIndex].tolerances[0] = {
      ...updatedCargos[cargoIndex].tolerances[0],
      [field]: value,
    };
    setFormData((prev) => ({ ...prev, cargos: updatedCargos }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = () => {
    // Filtrera bort tomma laster
    const validCargos = formData.cargos.filter((c) => c.cargo.trim() !== "");

    if (validCargos.length === 0 && formData.cargos[0].cargo === "") {
      alert("Vänligen lägg till minst en lasttyp.");
      return;
    }

    onSave({
      ...formData,
      cargos: validCargos,
      updatedAt: new Date().toISOString(),
    });
  };

  const sectionLabelStyle = {
    display: "flex",
    alignItems: "center",
    color: theme.palette.primary.main,
    fontWeight: 800,
    textTransform: "uppercase",
    letterSpacing: 1.5,
    fontSize: "0.75rem",
    mb: 2,
  };

  return (
    <Box sx={{ py: 1 }}>
      <Stack spacing={4}>
        {/* === TRIP DETAILS === */}
        <Paper
          elevation={0}
          sx={{
            p: 3,
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 3,
          }}
        >
          <Typography variant="subtitle2" sx={sectionLabelStyle}>
            <VoyageIcon sx={{ mr: 1.5, fontSize: 20 }} /> {t("tripDetails")}
          </Typography>

          <Grid container spacing={2.5}>
            <Grid size={{ xs: 12, sm: 4 }}>
              <FormControl fullWidth>
                <InputLabel>{t("operationTypeTitle")}</InputLabel>
                <Select
                  name="operationType"
                  value={formData.operationType}
                  label={t("operationTypeTitle")}
                  onChange={handleChange}
                >
                  <MenuItem value="Loading">{t("loading")}</MenuItem>
                  <MenuItem value="Discharging">{t("discharging")}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                label={t("toDestination")}
                fullWidth
                name="to"
                value={formData.to}
                onChange={handleChange}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                label={t("jetty")}
                fullWidth
                name="jetty"
                value={formData.jetty}
                onChange={handleChange}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                label={t("supplier/receiver")}
                fullWidth
                name="supplierReceiver"
                value={formData.supplierReceiver}
                onChange={handleChange}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <DatePicker
                label={t("date")}
                sx={{ width: "100%" }}
                value={formData.date ? dayjs(formData.date) : null}
                onChange={(newValue) =>
                  setFormData((prev) => ({
                    ...prev,
                    date: newValue ? dayjs(newValue).format("YYYY-MM-DD") : "",
                  }))
                }
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <DatePicker
                label={t("cpdate")}
                sx={{ width: "100%" }}
                value={formData.cpDated ? dayjs(formData.cpDated) : null}
                onChange={(newValue) =>
                  setFormData((prev) => ({
                    ...prev,
                    cpDated: newValue
                      ? dayjs(newValue).format("YYYY-MM-DD")
                      : "",
                  }))
                }
              />
            </Grid>
          </Grid>
        </Paper>

        {/* === CARGO INFO === */}
        <Paper
          elevation={0}
          sx={{
            p: 3,
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 3,
            bgcolor: alpha(theme.palette.primary.main, 0.02),
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 3,
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{ ...sectionLabelStyle, mb: 0 }}
            >
              <CargoIcon sx={{ mr: 1.5, fontSize: 20 }} />{" "}
              {t("cargoinformation")}
            </Typography>
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon />}
              onClick={addCargoRow}
              sx={{ borderRadius: 2, fontWeight: 700 }}
            >
              {t("add_cargo")}
            </Button>
          </Box>

          <Stack spacing={2}>
            {formData.cargos.map((cargo, index) => (
              <Box
                key={cargo.cargoId}
                sx={{
                  p: 2.5,
                  borderRadius: 2.5,
                  bgcolor: "background.paper",
                  border: "1px solid",
                  borderColor: "divider",
                  position: "relative",
                  "&:hover": { borderColor: theme.palette.primary.main },
                }}
              >
                {formData.cargos.length > 1 && (
                  <IconButton
                    size="small"
                    onClick={() => removeCargoRow(cargo.cargoId)}
                    sx={{
                      position: "absolute",
                      top: 8,
                      right: 8,
                      color: "error.light",
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                )}

                <Grid container spacing={2} alignItems="flex-start">
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <TextField
                      label={t("cargo")}
                      placeholder={t("cargoNamePlaceholder")}
                      fullWidth
                      size="small"
                      value={cargo.cargo}
                      onChange={(e) =>
                        handleCargoChange(index, "cargo", e.target.value)
                      }
                    />
                  </Grid>
                  <Grid size={{ xs: 6, sm: 2.5 }}>
                    <TextField
                      label={t("quantity")}
                      placeholder={t("quantityPlaceholder")}
                      fullWidth
                      size="small"
                      type="number"
                      value={cargo.quantity}
                      onChange={(e) =>
                        handleCargoChange(index, "quantity", e.target.value)
                      }
                    />
                  </Grid>
                  <Grid size={{ xs: 6, sm: 3 }}>
                    <FormControl fullWidth size="small">
                      <InputLabel>{t("unit")}</InputLabel>
                      <Select
                        value={cargo.unit}
                        label={t("unit")}
                        onChange={(e) =>
                          handleCargoChange(index, "unit", e.target.value)
                        }
                      >
                        <MenuItem value="m3 (obs)">m3 (obs)</MenuItem>
                        <MenuItem value="m3 (GSV)">m3 (GSV)</MenuItem>
                        <MenuItem value="Mt (Vac)">Mt (Vac)</MenuItem>
                        <MenuItem value="Mt (Air)">Mt (Air)</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 2.5 }}>
                    <Button
                      fullWidth
                      variant={cargo.showTolerance ? "contained" : "outlined"}
                      size="small"
                      onClick={() => toggleTolerance(index)}
                      sx={{
                        height: 40,
                        borderRadius: 1.5,
                        textTransform: "none",
                      }}
                    >
                      {cargo.showTolerance
                        ? t("removeTolerance")
                        : t("addTolerance")}
                    </Button>
                  </Grid>

                  {cargo.showTolerance && (
                    <Grid size={12}>
                      <Box
                        sx={{
                          mt: 1,
                          p: 2,
                          borderRadius: 2,
                          bgcolor: alpha(theme.palette.primary.main, 0.04),
                          display: "flex",
                          gap: 2,
                        }}
                      >
                        <TextField
                          label={t("toleranceValue")}
                          size="small"
                          type="number"
                          value={cargo.tolerances?.[0]?.toleranceValue || ""}
                          onChange={(e) =>
                            handleToleranceChange(
                              index,
                              "toleranceValue",
                              e.target.value
                            )
                          }
                        />
                        <FormControl size="small" sx={{ width: 100 }}>
                          <InputLabel>{t("unit")}</InputLabel>
                          <Select
                            value={cargo.tolerances?.[0]?.toleranceUnit || "%"}
                            label={t("unit")}
                            onChange={(e) =>
                              handleToleranceChange(
                                index,
                                "toleranceUnit",
                                e.target.value
                              )
                            }
                          >
                            <MenuItem value="%">%</MenuItem>
                            <MenuItem value="m3">m3</MenuItem>
                            <MenuItem value="Mt">Mt</MenuItem>
                          </Select>
                        </FormControl>
                      </Box>
                    </Grid>
                  )}
                </Grid>
              </Box>
            ))}
          </Stack>
        </Paper>

        {/* === CREW INFO === */}
        <Paper
          elevation={0}
          sx={{
            p: 3,
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 3,
          }}
        >
          <Typography variant="subtitle2" sx={sectionLabelStyle}>
            <CrewIcon sx={{ mr: 1.5, fontSize: 20 }} /> {t("crewInformation")}
          </Typography>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label={t("master")}
                fullWidth
                value={formData.crew.masterName}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    crew: { ...prev.crew, masterName: e.target.value },
                  }))
                }
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label={t("chiefOfficer")}
                fullWidth
                value={formData.crew.chiefOfficer}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    crew: { ...prev.crew, chiefOfficer: e.target.value },
                  }))
                }
              />
            </Grid>
          </Grid>
        </Paper>

        {/* === ACTIONS === */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 2,
            pt: 2,
            pb: 4,
          }}
        >
          <Button
            onClick={onCancel}
            variant="text"
            color="inherit"
            sx={{ fontWeight: 700 }}
          >
            {t("cancel")}
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            size="large"
            sx={{ px: 6, py: 1.5, fontWeight: 900, borderRadius: 2 }}
          >
            {t("saveTrip")}
          </Button>
        </Box>
      </Stack>
    </Box>
  );
};

export default TripForm;
